import type { SxProps, Theme } from '@mui/material/styles';
import colors from '../../common/colors';

export type SegmentedControlWidth = 'full' | 'inline';

export const labelSx: SxProps<Theme> = {
  color: colors.grey_100,
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '16px',
  mb: '4px',
};

/**
 * Styles for the MUI ToggleButtonGroup, built only from the app's existing
 * palette tokens:
 *   selected   = primary navy fill (grey_200) + white text
 *   unselected = grey_200 text on a muted (neutral_400) border
 *
 * Height defaults to the parent's height (`height: 100%`) and is never below
 * 48px, so the control lines up with sibling filter inputs.
 */
export const getGroupSx = (width: SegmentedControlWidth): SxProps<Theme> => ({
  backgroundColor: colors.white,
  height: '100%',
  minHeight: 48,
  // 'full' stretches to fill the parent (flex:1 per button); 'inline' hugs
  // content but sizes every segment to the widest one via equal 1fr grid
  // columns, so labels of different lengths share a single width.
  ...(width === 'full'
    ? { display: 'flex', width: '100%' }
    : {
        display: 'inline-grid',
        gridAutoFlow: 'column',
        gridAutoColumns: '1fr',
      }),
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: { xs: '12px', sm: '14px' },
    color: colors.grey_200,
    borderColor: colors.neutral_400,
    paddingInline: { xs: '8px', sm: '16px' },
    height: '100%',
    minHeight: 48,
    ...(width === 'full' ? { flex: 1 } : {}),
    '&:hover': {
      backgroundColor: colors.neutral_200,
    },
    '&.Mui-selected': {
      backgroundColor: colors.grey_200,
      color: colors.white,
      borderColor: colors.grey_200,
      '&:hover': {
        backgroundColor: colors.grey_300,
      },
    },
    '&.Mui-disabled': {
      color: colors.grey_100_50,
      borderColor: colors.neutral_300,
    },
  },
});
